
import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import CMSListingsCollection from '@reverbdotcom/commons/src/cms/components/cms_listings_collection';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { MostWatchedQuery, reverb_search_ListingsSearchRequest_Sort } from '@reverbdotcom/commons/src/rql';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import I18n from 'i18n-js';
import React from 'react';
import { connect } from '@reverbdotcom/commons/src/connect';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import EmptyStateWatchedListings from '@reverbdotcom/commons/src/components/empty_state_watched_listings';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';

export interface ExternalProps {
  handleCreateWatchResult?: () => void;
  hasWatchedListings?: boolean;
  emptyStateMainCallToAction?: React.ReactElement;
  emptyStateSecondaryCallToAction?: React.ReactElement;
}

type Props = ChildProps<IUserContext & ExternalProps, MostWatchedQuery.Query>;

export function WatchedListingsFallback(props: Props) {
  const { data, hasWatchedListings, user } = props;

  const topProductTypeUuids = data?.me?.profile?.topProductTypeUuids || [];
  const hasAffinities = topProductTypeUuids.length > 0;
  const redesignExperimentEnabled = isExperimentEnabled(user, experiments.PERSONALIZED_HOMEPAGE_REDESIGN);
  useViewTracking({
    eventName: MParticleEventName.ComponentView,
    componentName: hasAffinities ? 'HomepageMostWatched' : 'EmptyStateWatchedListings',
  }, (!data.loading && redesignExperimentEnabled));

  if (data.loading) { return null; }

  if (!redesignExperimentEnabled) {
    // control group users that have no listings see EmptyState, otherwise they see nothing
    if (hasWatchedListings) { return null; }
    return (
      <EmptyStateWatchedListings
        user={user}
        mainCallToAction={props.emptyStateMainCallToAction}
        secondaryCallToAction={props.emptyStateSecondaryCallToAction}
      />
    );
  }


  if (!hasAffinities) {
    // exp group users that have no affinities should see EmptyState with copy that changes depending on if they have watched listings
    return (
      <EmptyStateWatchedListings
        user={user}
        mainCallToAction={props.emptyStateMainCallToAction}
        secondaryCallToAction={props.emptyStateSecondaryCallToAction}
        innerSubtitleOverride={
          hasWatchedListings ?
            I18n.t('commons.watchRow.loggedIn.emptyState.intermediarySubtitle', { tagOpen: '<strong>', tagClose: '</strong>' })
            : null
        }
      />
    );
  }

  const topProductTypes = data?.me?.profile?.topProductTypes || [];
  const topProductAffinity = topProductTypeUuids[0];
  const categoryName = I18n.t(`discovery.taxonomy.${ topProductTypeUuids[0]}`);

  return (
    <CMSListingsCollection
      ctaTargetHref={Paths.marketplace.expand({ sort: 'watchers_count|desc', product_type: topProductTypes[0] })}
      categoryUuids={[topProductAffinity]}
      sort={reverb_search_ListingsSearchRequest_Sort.WATCHERS_COUNT_DESC}
      title={I18n.t('discovery.homepage.mostWatched', { categoryName: categoryName })}
      trackingName="HomepageMostWatched"
      userShippingRegion={user.shippingRegionCode}
      maxCount={8}
      largeTiles
      ssr={false}
      handleCreateWatchResult={props.handleCreateWatchResult}
      indentedMosaic
    />
  );
}

const withQuery = withGraphql<Props, MostWatchedQuery.Query>(
  gql`
    query Most_Watched_Query {
      me {
        uuid
        profile {
          topProductTypeUuids
          topProductTypes
        }
      }
    }
  `,
  {
    options: ownProps => ({
      ssr: false,
      skip: !isExperimentEnabled(ownProps.user, experiments.PERSONALIZED_HOMEPAGE_REDESIGN),
      variables: {},
    }),
  },
);

export const WatchedListingsFallbackRow = connect<ExternalProps>([
  withUserContext,
  withQuery,
])(WatchedListingsFallback);
